// Глобальное состояние клиента (zustand): сессия, город, ресурсы (с клиентской проекцией), походы,
// чат, уведомления, соединение. Сервер — источник истины; клиент только отображает.
import type { CastleView, ChatMessageView, IncomingAttack, MarchView, PublicUser } from '@/api/types';
import { RESOURCE_TYPES, type ResourceMap } from '@sk/shared';
import { create } from 'zustand';

export interface Toast {
  id: number;
  text: string;
  kind: 'info' | 'success' | 'error' | 'warning';
}

interface GameState {
  user: PublicUser | null;
  castle: CastleView | null;
  resources: ResourceMap | null;
  rates: ResourceMap | null;
  resourcesAt: number;
  gems: number;
  marches: MarchView[];
  incoming: IncomingAttack[];
  chat: Record<string, ChatMessageView[]>;
  toasts: Toast[];
  online: number;
  connected: boolean;
  worldSeed: number | null;
  worldSize: number;
  flags: string[];
  setUser: (u: PublicUser | null) => void;
  setCastle: (c: CastleView) => void;
  setResources: (r: ResourceMap, gems: number, rates?: ResourceMap) => void;
  setMarches: (own: MarchView[], incoming: GameState['incoming']) => void;
  addChat: (channel: string, m: ChatMessageView | ChatMessageView[], replace?: boolean) => void;
  removeChat: (id: string) => void;
  toast: (text: string, kind?: Toast['kind']) => void;
  dismiss: (id: number) => void;
  setConnected: (c: boolean) => void;
  setOnline: (n: number) => void;
  setWorld: (seed: number, size: number) => void;
  setFlags: (f: string[]) => void;
}

let toastId = 0;

export const useGame = create<GameState>((set, get) => ({
  user: null,
  castle: null,
  resources: null,
  rates: null,
  resourcesAt: Date.now(),
  gems: 0,
  marches: [],
  incoming: [],
  chat: {},
  toasts: [],
  online: 0,
  connected: false,
  worldSeed: null,
  worldSize: 200,
  flags: [],
  setUser: (user) => set({ user }),
  setCastle: (castle) =>
    set({
      castle,
      resources: castle.resources,
      rates: castle.rates,
      gems: castle.gems,
      resourcesAt: Date.now(),
    }),
  setResources: (resources, gems, rates) =>
    set({ resources, gems, rates: rates ?? get().rates, resourcesAt: Date.now() }),
  setMarches: (marches, incoming) => set({ marches, incoming }),
  addChat: (channel, m, replace) =>
    set((s) => {
      const list = Array.isArray(m) ? m : [m];
      const current = replace ? [] : (s.chat[channel] ?? []);
      const merged = [...current, ...list.filter((x) => !current.some((c) => c.id === x.id))].slice(-150);
      return { chat: { ...s.chat, [channel]: merged } };
    }),
  removeChat: (id) =>
    set((s) => ({
      chat: Object.fromEntries(Object.entries(s.chat).map(([k, v]) => [k, v.filter((m) => m.id !== id)])),
    })),
  toast: (text, kind = 'info') => {
    // Одно событие приходит и своим сообщением сокета, и общим уведомлением — окно одно
    if (get().toasts.some((t) => t.text === text)) return;
    const id = ++toastId;
    set((s) => ({ toasts: [...s.toasts.slice(-3), { id, text, kind }] }));
    setTimeout(() => get().dismiss(id), kind === 'error' ? 5000 : 3500);
  },
  dismiss: (id) => set((s) => ({ toasts: s.toasts.filter((t) => t.id !== id) })),
  setConnected: (connected) => set({ connected }),
  setOnline: (online) => set({ online }),
  setWorld: (worldSeed, worldSize) => set({ worldSeed, worldSize }),
  setFlags: (flags) => set({ flags }),
}));

/** Проекция ресурсов на текущий момент (rate × Δt), как на сервере: добыча не ограничена складом. */
export function projectResources(
  s: Pick<GameState, 'resources' | 'rates' | 'resourcesAt' | 'castle'>,
  now = Date.now(),
): ResourceMap | null {
  if (!s.resources || !s.rates) return s.resources;
  const hours = Math.min(8, (now - s.resourcesAt) / 3_600_000);
  const res = { ...s.resources };
  for (const t of RESOURCE_TYPES) {
    const rate = s.rates[t] ?? 0;
    const v = s.resources[t] + rate * (1 - (s.castle?.taxRate ?? 0)) * hours;
    res[t] = Math.max(0, v);
  }
  return res;
}
